import React, { useEffect, useRef } from 'react';
import {
    Animated,
    Image,
    PanResponder,
    Platform,
    StyleSheet,
    View,
    ViewStyle,
} from 'react-native';

// Whether the current platform uses the new "full-row" gesture model.
// On iOS the previous "right-third only" behavior was reported as flaky
// (touches near the edge often missed), so we activate the swipe across
// the entire row and add threshold + commit haptics. On Android the
// right-third behavior worked well — we keep it intact to avoid regressions.
const FULL_ROW_GESTURE = Platform.OS === 'ios';

/**
 * HideableAccountRow
 * ──────────────────
 * Wraps an AccountCard (or any child) in a horizontally-swipeable row whose
 * single behavior is "swipe left → toggle visibility".
 *
 * Behavior
 * ────────
 * • A tap with no horizontal movement passes through to the wrapped child —
 *   the existing onPress (e.g. open modal) keeps working.
 *
 * • A leftward drag past TRIGGER_PX (or a fast left-flick) calls onToggle()
 *   on release. Otherwise the row springs back to rest with no side effect.
 *   No reveal/tap two-step UI — one fluid gesture, one outcome.
 *
 * Visibility model
 * ────────────────
 * The component itself is stateless re: hidden — the parent passes the
 * current `isHidden` for context (e.g. to drive Undo messaging) and
 * `onToggle()` is invoked when the user wants to flip it.
 */

const TRIGGER_PX = 80;
const FLICK_VX = 0.5;
const ACTIVATION_DX = 6;
const RUBBER_BAND_CAP = 140;

interface Props {
    /** Current hidden state — supplied for parent-side messaging only. */
    isHidden: boolean;
    /** Called when the user confirms toggle by swiping past the threshold. */
    onToggle: () => void;
    /** Icon shown to the right during swipe. Defaults to closed eye. */
    swipeIcon?: 'eye' | 'eyeclose';
    /** Outer style applied to the whole row container. */
    style?: ViewStyle | ViewStyle[];
    /** Called when a horizontal swipe gesture is claimed — use to lock parent ScrollView. */
    onSwipeStart?: () => void;
    /** Called when the gesture ends — use to unlock parent ScrollView. */
    onSwipeEnd?: () => void;
    children: React.ReactNode;
}

const HideableAccountRow: React.FC<Props> = ({ isHidden, onToggle, swipeIcon = 'eyeclose', style, onSwipeStart, onSwipeEnd, children }) => {
    const translateX = useRef(new Animated.Value(0)).current;
    const iconOpacity = useRef(new Animated.Value(0)).current;
    // Android-only: width of the row, captured via onLayout, used to test
    // whether the touch began in the right third (the activation zone).
    const rowWidth = useRef(0);
    // Android-only: location.x of the original touch, used as the gating
    // coordinate for the right-third check. -1 means "not captured yet".
    const touchStartX = useRef(-1);

    const latestRef = useRef({ isHidden, onToggle, onSwipeStart, onSwipeEnd });
    useEffect(() => {
        latestRef.current = { isHidden, onToggle, onSwipeStart, onSwipeEnd };
    }, [isHidden, onToggle, onSwipeStart, onSwipeEnd]);

    const springBack = () => {
        Animated.spring(translateX, {
            toValue: 0,
            useNativeDriver: true,
            bounciness: 0,
            speed: 18,
        }).start();
        Animated.timing(iconOpacity, {
            toValue: 0,
            duration: 150,
            useNativeDriver: true,
        }).start();
    };

    const fireToggle = () => {
        springBack();
        setTimeout(() => latestRef.current.onToggle(), 30);
    };

    const panResponder = useRef(
        PanResponder.create({
            onStartShouldSetPanResponder: (evt) => {
                if (FULL_ROW_GESTURE) { return false; }
                touchStartX.current = evt.nativeEvent.locationX;
                return false;
            },
            onMoveShouldSetPanResponder: (evt, gesture) => {
                if (FULL_ROW_GESTURE) {
                    // iOS: the entire row is a swipe target. Claim the responder
                    // as soon as the user moves horizontally past ACTIVATION_DX,
                    // horizontal > vertical, and vertical movement is small
                    // (prevents diagonal gestures from triggering hide action).
                    return (
                        Math.abs(gesture.dx) > ACTIVATION_DX &&
                        Math.abs(gesture.dx) > Math.abs(gesture.dy) * 2 &&
                        Math.abs(gesture.dy) < 15
                    );
                }
                // Android: only activate if the touch began in the right third.
                const startX = touchStartX.current >= 0
                    ? touchStartX.current
                    : evt.nativeEvent.locationX;
                const inRightThird = rowWidth.current > 0
                    ? startX >= rowWidth.current * 2 / 3
                    : true;
                if (!inRightThird) { return false; }
                return (
                    Math.abs(gesture.dx) > ACTIVATION_DX &&
                    Math.abs(gesture.dx) > Math.abs(gesture.dy) * 2 &&
                    Math.abs(gesture.dy) < 15
                );
            },

            onPanResponderGrant: () => {
                touchStartX.current = -1;
                translateX.stopAnimation();
                latestRef.current.onSwipeStart?.();
            },

            onPanResponderMove: (_evt, gesture) => {
                let next = gesture.dx;
                if (next > 0) { next = 0; }
                if (next < -RUBBER_BAND_CAP) { next = -RUBBER_BAND_CAP; }
                translateX.setValue(next);
                // Иконка появляется плавно по мере свайпа (0 → 1 за первые 60px)
                iconOpacity.setValue(Math.min(Math.abs(next) / 60, 1));

            },

            onPanResponderRelease: (_evt, gesture) => {
                latestRef.current.onSwipeEnd?.();
                if (gesture.vx <= -FLICK_VX) {
                    fireToggle();
                    return;
                }
                if (gesture.dx <= -TRIGGER_PX) {
                    fireToggle();
                    return;
                }
                springBack();
            },

            onPanResponderTerminate: () => {
                touchStartX.current = -1;
                latestRef.current.onSwipeEnd?.();
                springBack();
            },
        }),
    ).current;

    return (
        <View
            style={[styles.container, style]}
            // onLayout is only used by the Android right-third gating, but
            // we attach it unconditionally — it's cheap and keeps the JSX
            // straightforward. iOS simply ignores rowWidth.
            onLayout={(e) => { rowWidth.current = e.nativeEvent.layout.width; }}
            {...panResponder.panHandlers}
        >
            <Animated.View style={[styles.cardRow, { transform: [{ translateX }] }]}>
                {children}
                {/* Иконка глаза — появляется справа при свайпе, центрирована по высоте плашки */}
                <Animated.View style={[styles.iconWrap, { opacity: iconOpacity }]} pointerEvents="none">
                    <Image
                        source={swipeIcon === 'eye'
                            ? require('../../assets/eye.png')
                            : require('../../assets/eyeclose.png')}
                        style={styles.eyeIcon}
                        resizeMode="contain"
                    />
                </Animated.View>
            </Animated.View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        position: 'relative',
    },
    cardRow: {
        // Обёртка плашки + иконки, движется как единое целое при свайпе
    },
    iconWrap: {
        position: 'absolute',
        right: -44,       // 20px gap + 24px (половина иконки)
        top: 0,
        bottom: 12,       // компенсируем marginBottom плашки (12px) чтобы иконка была по центру карточки
        width: 28,
        alignItems: 'center',
        justifyContent: 'center',
    },
    eyeIcon: {
        width: 28,
        height: 28,
        tintColor: '#888888',
    },
});

export default HideableAccountRow;
